import React, { createContext, useContext } from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { disableLayoutAnimation } from 'components/contentRegister/components/sortableTable/helpers/sortables';

// The handle is rendered by a column cell rather than by the row, so the sortable is
// registered once here and its activator props handed down instead of the cell registering
// a second droppable under the same id. The activator attributes belong on the handle, not
// the row: on the <tr> their role="button" would replace the row's table semantics.
const SortableRowContext = createContext(null);

export function useSortableRow() {
  const context = useContext(SortableRowContext);

  if (!context) {
    throw new Error('useSortableRow must be used within a sortable row');
  }

  return context;
}

export function useSortableRowNode({ elementId, isSortable, sortableId }) {
  const {
    attributes: dragAttributes,
    isDragging,
    listeners: dragListeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: sortableId,
    animateLayoutChanges: disableLayoutAnimation,
    data: { elementId },
    disabled: !isSortable,
  });

  return {
    dragAttributes,
    dragListeners,
    isDragging,
    setActivatorNodeRef,
    setNodeRef,
    style: { transform: CSS.Transform.toString(transform), transition },
  };
}

export default function SortableRow({
  children,
  className,
  elementId,
  isSortable = true,
  sortableId,
  ...rowProps
}) {
  const {
    dragAttributes,
    dragListeners,
    isDragging,
    setActivatorNodeRef,
    setNodeRef,
    style,
  } = useSortableRowNode({ elementId, isSortable, sortableId });

  return (
    <SortableRowContext.Provider
      value={{
        dragAttributes,
        dragListeners,
        isDragging,
        isSortable,
        setActivatorNodeRef,
      }}
    >
      {/* Invisible rather than absent: the row keeps its place and its displacement, so
          the ghost slot showing through is where it will drop. */}
      <tr
        {...rowProps}
        className={classNames(className, isDragging && 'tw-opacity-0')}
        id={elementId}
        ref={setNodeRef}
        style={style}
      >
        {children}
      </tr>
    </SortableRowContext.Provider>
  );
}

SortableRow.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  elementId: PropTypes.string.isRequired,
  isSortable: PropTypes.bool,
  sortableId: PropTypes.string.isRequired,
};

export { SortableRowContext };
